import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { 
  ChartBarIcon,
  ArrowDownTrayIcon,
  CurrencyDollarIcon,
  ArrowUpIcon,
  CalendarIcon,
  ArrowDownIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import apiService from '../../services/api';

interface InvoiceStats {
  totalInvoices: number;
  totalRevenue: number;
  paidInvoices: number;
  overdueInvoices: number;
  pendingAmount: number;
  paidAmount: number;
  overdueAmount: number;
  averagePaymentTime: number;
}

interface MonthlyData {
  month: string;
  year: number;
  totalInvoices: number;
  revenue: number;
  paidAmount: number;
  overdueAmount: number;
  paidInvoices: number;
  overdueInvoices: number;
}

export const AccountingReports = () => {
  const [stats, setStats] = useState<InvoiceStats | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReportsData();
  }, []);

  const fetchReportsData = async () => {
    try {
      setLoading(true);

      // Fetch invoice statistics
      const statsResponse = await apiService.get('/invoices/stats');
      console.log('Stats response:', statsResponse.data);
      setStats(statsResponse.data);

      // Fetch real monthly data
      const monthlyResponse = await apiService.get('/invoices/monthly-stats?months=7');
      console.log('Monthly data response:', monthlyResponse.data);
      setMonthlyData(monthlyResponse.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch reports data');
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const reportTypes = [
    {
      id: 1,
      name: 'Raport miesięczny',
      description: 'Przychody i płatności z podziałem na miesiące',
      icon: CalendarIcon,
      lastGenerated: new Date().toISOString().split('T')[0]
    },
    {
      id: 2,
      name: 'Analiza płatności',
      description: 'Status płatności faktur i zaległości',
      icon: CurrencyDollarIcon,
      lastGenerated: new Date().toISOString().split('T')[0]
    },
    {
      id: 3,
      name: 'Raport klientów',
      description: 'Analiza najważniejszych klientów i ich płatności',
      icon: ChartBarIcon,
      lastGenerated: new Date().toISOString().split('T')[0]
    }
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pl-PL');
  };

  const calculatePercentageChange = (current: number, previous: number) => {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Ładowanie raportów...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchReportsData} variant="outline">
          Spróbuj ponownie
        </Button>
      </div>
    );
  }

  if (!stats) {
    return <div>Brak danych do wyświetlenia</div>;
  }

  const currentMonth = monthlyData.length > 0 ? monthlyData[monthlyData.length - 1] : null;
  const previousMonth = monthlyData.length > 1 ? monthlyData[monthlyData.length - 2] : null;

  const revenueChange = currentMonth && previousMonth ? 
    calculatePercentageChange(currentMonth.revenue, previousMonth.revenue) : 0;
  const paymentRate = stats.totalInvoices > 0 ? 
    (stats.paidInvoices / stats.totalInvoices) * 100 : 0;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Raporty finansowe</h1>
        <p className="mt-1 text-sm text-gray-500">
          Analizy i raporty finansowe firmy
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Łączne przychody
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {formatCurrency(stats.totalRevenue)}
                  </dd>
                  <dd className="flex items-center text-sm text-green-600">
                    <ArrowUpIcon className="h-4 w-4 mr-1" />
                    +{revenueChange.toFixed(1)}%
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ArrowUpIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Opłacone faktury
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {formatCurrency(stats.paidAmount)}
                  </dd>
                  <dd className="text-sm text-gray-500">
                    {stats.paidInvoices} z {stats.totalInvoices} faktur
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 font-bold">%</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Wskaźnik płatności
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {paymentRate.toFixed(1)}%
                  </dd>
                  <dd className="text-sm text-gray-500">
                    Faktury opłacone na czas
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-indigo-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Zaległe płatności
                  </dt>
                  <dd className="text-lg font-semibold text-red-600">
                    {formatCurrency(stats.overdueAmount)}
                  </dd>
                  <dd className="text-sm text-gray-500">
                    {stats.overdueInvoices} faktur
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Monthly Performance Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Wyniki miesięczne (2025)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {monthlyData.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  Brak danych miesięcznych
                </div>
              ) : (
                monthlyData.slice(-6).map((data, index) => (
                <div key={`${data.month}-${data.year}`} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{data.month} {data.year}</div>
                    <div className="text-xs text-gray-500">
                      Faktury: {data.totalInvoices} | Przychody: {formatCurrency(data.revenue)}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm font-medium text-green-600">
                      {formatCurrency(data.paidAmount)}
                    </div>
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{
                          width: `${monthlyData.length > 0 && Math.max(...monthlyData.map(m => m.paidAmount)) > 0 ? (data.paidAmount / Math.max(...monthlyData.map(m => m.paidAmount))) * 100 : 0}%`
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Report Generation */}
        <Card>
          <CardHeader>
            <CardTitle>Dostępne raporty</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {reportTypes.map((report) => (
                <div key={report.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <report.icon className="h-6 w-6 text-gray-600" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{report.name}</div>
                      <div className="text-xs text-gray-500">{report.description}</div>
                      <div className="text-xs text-gray-400">
                        Ostatnio: {formatDate(report.lastGenerated)}
                      </div>
                    </div>
                  </div>
                  <Button size="sm" variant="outline">
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    Pobierz
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analytics */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Analiza przychodów i płatności</CardTitle>
            <div className="flex space-x-2">
              <Button variant="outline">
                <CalendarIcon className="h-4 w-4 mr-2" />
                Zmień okres
              </Button>
              <Button>
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Eksportuj do Excel
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Miesiąc
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Faktury
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Przychody
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Opłacone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Zaległe
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Wskaźnik płatności
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {monthlyData.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      Brak danych miesięcznych do wyświetlenia
                    </td>
                  </tr>
                ) : (
                  monthlyData.map((data, index) => {
                  const paymentRate = data.revenue > 0 ? (data.paidAmount / data.revenue) * 100 : 0;

                  return (
                    <tr key={`${data.month}-${data.year}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {data.month} {data.year}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {data.totalInvoices}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(data.revenue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        {formatCurrency(data.paidAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                        {formatCurrency(data.overdueAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className={`flex items-center ${paymentRate >= 80 ? 'text-green-600' : paymentRate >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {paymentRate.toFixed(1)}%
                        </div>
                      </td>
                    </tr>
                  );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};